import { forwardRef, InputHTMLAttributes, ReactNode, useId } from 'react';

import cn from '@/utils/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  // Optional inline label (renders the input + <label> as a row). Omit it to get
  // a bare styled checkbox (shadcn primitive behaviour).
  label?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, id, className, ...props }, ref) => {
    const generatedId = useId();
    const checkboxId = id ?? generatedId;

    const input = (
      <input
        ref={ref}
        id={checkboxId}
        type="checkbox"
        className={cn(
          'h-4 w-4 shrink-0 cursor-pointer rounded border-input text-primary accent-primary',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    );

    // A bare checkbox is a 16x16 target. Wrapping it in a plain <span> would
    // only reserve space - a span does not forward clicks to the input, unlike
    // a <label> - so callers that render one without a label (table
    // select-all / row-select) should wrap it in their own <label> or pass
    // `label`. Left as-is here rather than faking a larger hit area.
    if (!label) return input;

    return (
      <div className="flex min-h-11 items-center gap-2 sm:min-h-0">
        {input}
        <label htmlFor={checkboxId} className="cursor-pointer text-sm text-foreground">
          {label}
        </label>
      </div>
    );
  },
);
Checkbox.displayName = 'Checkbox';
